<script setup>
import TheCustomerMetaNavigation from '@/components/basic/atoms/TheCustomerMetaNavigation.vue'
import UspBar from '@/components/basic/atoms/UspBar.vue'
import TheHeader from '@/components/basic/organisms/TheHeader.vue'
import TheFilter from '@/components/category/organisms/TheFilter.vue'
import { translate } from '@/plugins/i18n.js'
import { NO_INDEX_FOLLOW } from '@/services/helpers/MetaHelper.js'
import { useSearchConfig } from '@/stores/searchConfig'
import { useHead } from '@unhead/vue'
import { computed, defineAsyncComponent, nextTick, watch } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'

const LazyComponent = defineAsyncComponent(
  () => import('@/components/basic/atoms/LazyComponent.vue')
)
const AppBanner = defineAsyncComponent(
  () => import('@/components/basic/molecules/AppBanner.vue')
)
const TheFooter = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheFooter.vue')
)
const ManufacturerChip = defineAsyncComponent(
  () => import('@/components/basic/atoms/ManufacturerChip.vue')
)

const searchConfig = useSearchConfig()
const route = useRoute()

const category = computed(() => searchConfig.category)
const categoryError = computed(() => searchConfig.config.error)
const manufacturerHits = computed(() => searchConfig.manufacturerHits)
const searchTerm = computed(() => route.query.q ?? '')

const headline = computed(() => {
  if (!searchTerm.value) return translate('fe_app_search_results')
  // Count only once results are in; while loading show the plain headline.
  if (searchConfig.categoryIsLoading || !category.value.code) {
    return translate('fe_app_search_results_for', searchTerm.value)
  }
  const total = searchConfig.getConfig.totalCount
  if (total === 0) {
    return translate('fe_app_search_results_for_none', searchTerm.value)
  }
  if (total === 1) {
    return translate('fe_app_search_results_for_one', searchTerm.value)
  }
  return translate('fe_app_search_results_for_many', total, searchTerm.value)
})

// Without a search term the BE responds 400; render the empty state instead.
if (searchTerm.value) {
  searchConfig.fetchGlobalSearch()
}

watch(
  () => route.query.q,
  async (newQ, oldQ) => {
    if (newQ !== oldQ) {
      searchConfig.resetConfig()
      if (!newQ) return
      await nextTick()
      await searchConfig.fetchGlobalSearch()
    }
  }
)

onBeforeRouteLeave(() => {
  searchConfig.resetConfig()
})

useHead({
  title: computed(() =>
    searchTerm.value
      ? translate('fe_app_search_results_for', searchTerm.value)
      : translate('fe_app_search_results')
  ),
  meta: [
    {
      name: 'robots',
      content: NO_INDEX_FOLLOW
    }
  ]
})
</script>

<template>
  <div
    class="position-relative"
    id="content"
    data-elbglobals="page_type:search;search_version:opensearch"
    data-elbcontext="shopping:discover"
  >
    <AppBanner />
    <UspBar />
    <TheCustomerMetaNavigation />
    <TheHeader class="position-relative" />
    <main id="page-template">
      <div class="v-container my-5">
        <div class="v-col-12 py-5">
          <h1
            class="font-garbata text-center font-size-xxxxl font-size-md-xxxxxl font-weight-bold c-winered"
          >
            {{ headline }}
          </h1>
        </div>
      </div>
      <div
        v-if="searchTerm && manufacturerHits.length"
        class="v-container mb-6"
      >
        <h2 class="font-garbata font-size-xxl font-weight-bold c-winered mb-3">
          {{ translate('fe_app_search_wineries') }}
        </h2>
        <div class="manufacturer-strip d-flex">
          <ManufacturerChip
            v-for="man in manufacturerHits"
            :key="`man-${man.manufacturer_id}`"
            :manufacturer="man"
            class="flex-shrink-0"
          />
        </div>
      </div>
      <p v-if="categoryError" class="text-center py-10">
        {{ translate('cms_loading_error') }}
      </p>
      <TheFilter v-else-if="searchTerm && category.code" />
      <div v-else-if="!searchTerm" class="v-container text-center py-10">
        <p>{{ translate('fe_app_search_enter_term') }}</p>
      </div>
    </main>
    <LazyComponent id="footer" :delay="2000">
      <TheFooter />
    </LazyComponent>
  </div>
</template>

<style scoped>
.manufacturer-strip {
  gap: 12px;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
}

.manufacturer-strip > * {
  scroll-snap-align: start;
  flex-shrink: 0;
}
</style>
